import Component from '../components/Component';
import Container from '../components/Container';
import Error from '../components/Error';
import TestCard from '../components/TestCard';
import FilterInput from '../components/FilterInput';
import Markdown from '../components/Markdown';
import Icon from '../components/Icon';
import Modal from '../components/Modal';

class AndroidTests extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tests: [],
      filteredTests: [],
      selectedTest: null,
      filterTestName: '',
      filterAppName: '',
      loading: true,
      error: null,
      modalImage: null
    };
  }

  async componentDidMount() {
    try {
      // Fetch the index of available test sessions
      const indexResponse = await fetch('/assets/android-tests/index.json');
      const index = await indexResponse.json();

      // Fetch data for each test session
      const testsData = await Promise.all(
        index.sessions.map(async (sessionId) => {
          try {
            const response = await fetch(`/assets/android-tests/${sessionId}/data.json`);
            if (!response.ok) throw new Error('Failed to load');
            return await response.json();
          } catch (err) {
            console.error(`Failed to load ${sessionId}:`, err);
            return null;
          }
        })
      );

      // Filter out failed loads and sort by date (newest first)
      const validTests = testsData.filter(test => test !== null);
      const sortedTests = validTests.sort((a, b) => b.start_time - a.start_time);

      this.setState({
        tests: sortedTests,
        filteredTests: sortedTests,
        loading: false
      });
    } catch (error) {
      this.setState({
        error: 'Failed to load test sessions',
        loading: false
      });
    }
  }

  applyFilters = () => {
    const { tests, filterTestName, filterAppName } = this.state;

    let filtered = tests;

    if (filterTestName) {
      filtered = filtered.filter(test =>
        test.test_name.toLowerCase().includes(filterTestName.toLowerCase())
      );
    }

    if (filterAppName) {
      filtered = filtered.filter(test =>
        test.app_name.toLowerCase().includes(filterAppName.toLowerCase())
      );
    }

    this.setState({ filteredTests: filtered });
  };

  handleFilterChange = (field, value) => {
    this.setState({ [field]: value }, this.applyFilters);
  };

  selectTest = (test) => {
    this.setState({ selectedTest: test });
  };

  backToList = () => {
    this.setState({ selectedTest: null });
  };

  openImageModal = (imageSrc) => {
    this.setState({ modalImage: imageSrc });
  };

  closeImageModal = () => {
    this.setState({ modalImage: null });
  };

  formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  formatDuration = (startTime, endTime) => {
    const duration = endTime - startTime;
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}m ${seconds}s`;
  };

  renderTestList = () => {
    const { filteredTests, filterTestName, filterAppName } = this.state;

    return (
      <div className="android-tests-list">
        <div className="left-header mb2">Android Tests</div>

        <div className="test-filters">
          <FilterInput
            placeholder="Filter by test name..."
            value={filterTestName}
            onChange={(value) => this.handleFilterChange('filterTestName', value)}
          />
          <FilterInput
            placeholder="Filter by app name..."
            value={filterAppName}
            onChange={(value) => this.handleFilterChange('filterAppName', value)}
          />
        </div>

        <div className="test-count">
          Showing {filteredTests.length} test{filteredTests.length !== 1 ? 's' : ''}
        </div>

        <div className="tests-grid">
          {filteredTests.map((test, index) => (
            <TestCard
              key={index}
              test={test}
              onSelect={this.selectTest}
              formatDate={this.formatDate}
              formatDuration={this.formatDuration}
            />
          ))}
        </div>
      </div>
    );
  };

  renderTestDetail = () => {
    const { selectedTest } = this.state;

    return (
      <div className="test-detail">
        <div className="test-detail-header">
          <div className="row a-center">
            <Icon icon="arrow-left-long" onClick={this.backToList} className="mr2 opacity-hover" />
            <div className="left-header">{selectedTest.test_name}</div>
          </div>
          <span className={`status-badge large ${selectedTest.success ? 'success' : 'failure'}`}>
            {selectedTest.success ? '✓ PASSED' : '✗ FAILED'}
          </span>
        </div>

        <div className="test-detail-meta">
          <div className="meta-item">
            <strong>App:</strong> {selectedTest.app_name}
          </div>
          <div className="meta-item">
            <strong>Date:</strong> {this.formatDate(selectedTest.start_time)}
          </div>
          <div className="meta-item">
            <strong>Duration:</strong> {this.formatDuration(selectedTest.start_time, selectedTest.end_time)}
          </div>
          <div className="meta-item">
            <strong>Steps:</strong> {selectedTest.test_steps.length}
          </div>
        </div>

        {selectedTest.recording ? (
          <div className="test-assessment-recording-row">
            <div className="test-assessment test-section">
              <div className="left-header mb2">Assessment</div>
              <div className="assessment-text">
                <Markdown children={selectedTest.assessment} />
              </div>
            </div>

            <div className="test-recording test-section">
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <video
                  controls
                  style={{ maxWidth: '350px', borderRadius: '8px' }}
                  src={`/assets/android-tests/${selectedTest.session_id}/${selectedTest.recording}`}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        ) : (
          <div className="test-assessment test-section">
            <div className="left-header mb2">Assessment</div>
            <div className="assessment-text">
              <Markdown children={selectedTest.assessment} />
            </div>
          </div>
        )}

        <div className="test-screenshots">
          <div className="left-header mb2">Test Steps ({selectedTest.test_steps.length})</div>
          <div className="screenshot-steps-grid">
            {selectedTest.test_steps.map((step, index) => {
              const imageSrc = `/assets/android-tests/${selectedTest.session_id}/${step.screenshot}`;
              return (
                <div key={index} className="screenshot-step">
                  <div className="step-header">
                    <h3>Step {index + 1}: {step.action_type}</h3>
                    <span className="step-time">
                      {this.formatDate(step.timestamp)}
                    </span>
                  </div>
                  <img
                    src={imageSrc}
                    alt={`Step ${index + 1} - ${step.action_type}`}
                    className="screenshot-image"
                    onClick={() => this.openImageModal(imageSrc)}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { loading, error, selectedTest, modalImage } = this.state;

    return (
      <Container page="Android Tests" className="android-tests-page">
        <div className="body">
          {loading && <div className="loading">Loading test sessions...</div>}
          {error && <Error msg={error} />}
          {!loading && !error && (
            selectedTest ? this.renderTestDetail() : this.renderTestList()
          )}
        </div>

        {modalImage && (
          <Modal onClose={this.closeImageModal}>
            <img src={modalImage} alt="Enlarged screenshot" className="modal-image" />
          </Modal>
        )}
      </Container>
    );
  }
}

export default AndroidTests;
